import { NumericComparisonOperator } from '@ViewModels';
import type {
	QuestionVisibility,
	QuestionVisibilityComparison,
	QuestionVisibilitySimple,
	SurveyPart,
	SurveyQuestion,
	SurveySection,
} from '../types';
import {
	comparisonNeverFires,
	firingRangeForOperator,
	resolveNumericQuestionBounds,
	wholeThresholdEquivalent,
} from '../../../../../../../surveys/utils/scaleQuestions';
import { NUMERIC_SOURCE_TYPES, OPTION_SOURCE_TYPES, isSurveySection } from '../types';

/**
 * Validation helpers that mirror the BE SurveySection invariants enforced in
 * `Levitate.Api.Models/Surveys/Survey.cs:205-240`. Run FE-side to avoid 400 round-trips.
 */

export type ValidationCode = 'EMPTY_SECTION' | 'MULTI_FIELD_TRIGGER' | 'TRIGGER_AFTER_SECTION' | 'INVALID_TRIGGER_TYPE';

export interface SectionValidationError {
	code: ValidationCode;
	message: string;
}

const TRIGGER_TYPES = new Set([...OPTION_SOURCE_TYPES, ...NUMERIC_SOURCE_TYPES]);

type LeafVisibility = QuestionVisibilitySimple | QuestionVisibilityComparison;

const isLeaf = (v: QuestionVisibility): v is LeafVisibility => v._type !== 'LogicalQuestionVisibility';

function requiredSourceTypes(v: QuestionVisibility | undefined): ReadonlySet<string> | undefined {
	if (!v) return undefined;
	if (isLeaf(v)) return v._type === 'ComparisonQuestionVisibility' ? NUMERIC_SOURCE_TYPES : OPTION_SOURCE_TYPES;
	for (const child of v.visibilities ?? []) {
		const found = requiredSourceTypes(child);
		if (found) return found;
	}
	return undefined;
}

function getReferencedFieldIds(v: QuestionVisibility | undefined): string[] {
	if (!v) return [];
	if (isLeaf(v)) return [v.fieldId];
	return (v.visibilities ?? []).flatMap(getReferencedFieldIds);
}

/** All questions that appear earlier than `section` in form order (flat traversal). */
function getQuestionsBeforeSection(section: SurveySection, allParts: SurveyPart[]): SurveyQuestion[] {
	const before: SurveyQuestion[] = [];
	for (const part of allParts) {
		if (isSurveySection(part)) {
			if (part.id === section.id) break;
			before.push(...part.questions);
		} else {
			before.push(part);
		}
	}
	return before;
}

/**
 * Validate a single section against the four BE invariants. Returns the first
 * violation found, or `null` if the section is valid.
 */
export function validateSection(section: SurveySection, allParts: SurveyPart[]): SectionValidationError | null {
	if (!section.questions || section.questions.length === 0) {
		return { code: 'EMPTY_SECTION', message: 'Add a question to this section before saving.' };
	}

	if (section.visibleWhen) {
		const refs = Array.from(new Set(getReferencedFieldIds(section.visibleWhen)));
		if (refs.length > 1) {
			return {
				code: 'MULTI_FIELD_TRIGGER',
				message: 'All triggers on a section must reference the same question.',
			};
		}
		const triggerFieldId = refs[0];
		const beforeQuestions = getQuestionsBeforeSection(section, allParts);
		const triggerSource = beforeQuestions.find(q => q.fieldId === triggerFieldId);
		if (!triggerSource) {
			return {
				code: 'TRIGGER_AFTER_SECTION',
				message: 'Trigger question must appear before this section.',
			};
		}
		// What THIS rule can read, not the union: this gates Save, so it must agree with the sweep.
		const allowedSourceTypes = requiredSourceTypes(section.visibleWhen) ?? TRIGGER_TYPES;
		if (!allowedSourceTypes.has(triggerSource._type)) {
			return {
				code: 'INVALID_TRIGGER_TYPE',
				message:
					allowedSourceTypes === NUMERIC_SOURCE_TYPES
						? 'Trigger question must be a scale or NPS question.'
						: 'Trigger question must be a dropdown or checkbox.',
			};
		}
	}

	return null;
}

/**
 * V2 mirror of V1's `cleanupConditionalVisibility` (AdditionalQuestions/index.tsx:329).
 *
 * Pure function that walks every section-level and question-level `visibleWhen`
 * rule and drops references that are no longer valid because of any of:
 *  1. Source question was deleted (fieldId no longer in `parts`)
 *  2. Source question's `_type` changed away from a kind the rule can read —
 *     Dropdown/Checkbox for an equality rule, LinearScale/NPS for a comparison rule
 *  3. Source question now sits at/after the dependent in document order
 *     (violates the BE "trigger must precede" invariant)
 *  4. For a Simple rule's `fieldValue`, or each branch of a Logical OR,
 *     the option id no longer exists OR its label is empty
 *
 * Logical rules whose branches all fail are dropped; a Logical whose surviving
 * list has exactly 1 element collapses back to a Simple rule.
 *
 * Returns a new `SurveyPart[]` — input is not mutated. Idempotent.
 */
export function cleanupVisibilityRules(parts: SurveyPart[]): SurveyPart[] {
	interface FieldInfo {
		type: string;
		options: ReadonlyArray<{ id?: string; label?: string }>;
		/** Section index of this question; -1 for flat (legacy non-sectioned) parts. */
		sectionIndex: number;
		/** Absolute position across the entire flat traversal of parts. */
		flatPosition: number;
		/** Scale bounds, for the numeric question types only. */
		minValue?: number;
		maxValue?: number;
	}

	const index = new Map<string, FieldInfo>();
	let flat = 0;
	for (let i = 0; i < parts.length; i++) {
		const part = parts[i];
		if (isSurveySection(part)) {
			for (const q of part.questions ?? []) {
				index.set(q.fieldId, {
					type: q._type,
					options: (q as { options?: { id?: string; label?: string }[] }).options ?? [],
					sectionIndex: i,
					flatPosition: flat++,
					minValue: (q as { minValue?: number }).minValue,
					maxValue: (q as { maxValue?: number }).maxValue,
				});
			}
		} else if (part) {
			const q = part as SurveyQuestion;
			index.set(q.fieldId, {
				type: q._type,
				options: (q as { options?: { id?: string; label?: string }[] }).options ?? [],
				sectionIndex: -1,
				flatPosition: flat++,
				minValue: (q as { minValue?: number }).minValue,
				maxValue: (q as { maxValue?: number }).maxValue,
			});
		}
	}

	const isValidSimple = (
		rule: { _type: 'SimpleEqualityQuestionVisibility'; fieldId: string; fieldValue: string },
		precedes: (source: FieldInfo) => boolean
	): boolean => {
		const source = index.get(rule.fieldId);
		if (!source) return false;
		if (!OPTION_SOURCE_TYPES.has(source.type)) return false;
		if (!precedes(source)) return false;
		const optOk = source.options.some(o => (o.id ?? o.label) === rule.fieldValue && !!o.label?.trim());
		return optOk;
	};

	// Dropping instead would flip the question from never-shown to always-shown.
	const repair = (
		rule: QuestionVisibilityComparison,
		from: number,
		bounds: { min: number; max: number }
	): QuestionVisibilityComparison | undefined => {
		const firing = firingRangeForOperator(bounds, rule.op);
		const value = Math.min(Math.max(from, firing.min), firing.max);
		return comparisonNeverFires(rule.op, value, bounds) ? undefined : { ...rule, value };
	};

	// A rule every answer satisfies is left alone: the evaluator also requires the source to be
	// visible and answered, so deleting it would reveal the question before any answer is given.
	const resolveComparison = (
		rule: QuestionVisibilityComparison,
		precedes: (source: FieldInfo) => boolean
	): QuestionVisibilityComparison | undefined => {
		const source = index.get(rule.fieldId);
		if (!source) return undefined;
		if (!NUMERIC_SOURCE_TYPES.has(source.type)) return undefined;
		if (!precedes(source)) return undefined;
		if (!Number.isFinite(rule.value)) return undefined;
		const bounds = resolveNumericQuestionBounds(source.type, source.minValue, source.maxValue);
		if (!bounds) return rule;

		const whole = wholeThresholdEquivalent(rule.op, rule.value);
		// No whole-number equivalent: a fractional `!=` matches every answer, a fractional `=` none.
		if (whole === null) {
			return rule.op === NumericComparisonOperator.NotEqual ? rule : repair(rule, Math.round(rule.value), bounds);
		}
		if (!comparisonNeverFires(rule.op, whole, bounds)) {
			return whole === rule.value ? rule : { ...rule, value: whole };
		}
		return repair(rule, whole, bounds);
	};

	const resolveLeaf = (rule: LeafVisibility, precedes: (source: FieldInfo) => boolean): LeafVisibility | undefined => {
		if (rule._type === 'ComparisonQuestionVisibility') return resolveComparison(rule, precedes);
		return isValidSimple(rule, precedes) ? rule : undefined;
	};

	const clean = (
		v: QuestionVisibility | undefined,
		precedes: (source: FieldInfo) => boolean
	): QuestionVisibility | undefined => {
		if (!v) return undefined;
		if (isLeaf(v)) {
			return resolveLeaf(v, precedes);
		}
		const children = v.visibilities ?? [];
		let kept = children
			.filter(isLeaf)
			.map(child => resolveLeaf(child, precedes))
			.filter((rule): rule is LeafVisibility => !!rule);
		let lostBranches = children.length - kept.length;
		// Cap the surviving OR'd branches at the number of valid options. Dropdown
		// and Checkbox sources both cap at `validOptions` — a rule may reference up
		// to every option. Trims only genuine excess (e.g. duplicate/stale branches),
		// never a legitimate full set. Comparison branches have no option list to cap against.
		if (kept.length > 0 && kept.every(child => child._type === 'SimpleEqualityQuestionVisibility')) {
			const source = index.get(kept[0].fieldId);
			if (source) {
				const cap = source.options.filter(o => !!o.label?.trim()).length;
				if (kept.length > cap) {
					lostBranches += kept.length - cap;
					kept = kept.slice(0, cap);
				}
			}
		}
		if (kept.length === 0) return undefined;
		// An And is an intersection: keeping one bound after losing the other widens what it admits.
		// Or narrows correctly, so it still collapses.
		if (lostBranches > 0 && v.logicalOperator === 'And') return undefined;
		// Collapse single-entry Logical back to Simple to stay consistent with writers.
		if (kept.length === 1) return kept[0];
		return { ...v, visibilities: kept };
	};

	return parts.map((part, sectionIdx) => {
		if (isSurveySection(part)) {
			// Section-level rules: source must live in a strictly earlier section.
			const sectionPrecedes = (s: FieldInfo) => s.sectionIndex >= 0 && s.sectionIndex < sectionIdx;
			return {
				...part,
				visibleWhen: clean(part.visibleWhen, sectionPrecedes),
				questions: (part.questions ?? []).map(q => {
					const myPos = index.get(q.fieldId)?.flatPosition ?? Number.MAX_SAFE_INTEGER;
					const questionPrecedes = (s: FieldInfo) => s.flatPosition < myPos;
					return { ...q, visibleWhen: clean(q.visibleWhen, questionPrecedes) };
				}),
			};
		}
		const q = part as SurveyQuestion;
		const myPos = index.get(q.fieldId)?.flatPosition ?? Number.MAX_SAFE_INTEGER;
		const questionPrecedes = (s: FieldInfo) => s.flatPosition < myPos;
		return { ...part, visibleWhen: clean(q.visibleWhen, questionPrecedes) };
	});
}

/**
 * RHF integration: read `surveyForm.surveyParts`, compute the cleaned shape via
 * `cleanupVisibilityRules`, then issue surgical `setValue` calls for each
 * `visibleWhen` path that actually changed.
 *
 * Surgical updates (not a single setValue of the whole array) keep
 * `useFieldArray`'s `_rhfId` keys stable — otherwise every SectionCard would
 * unmount/remount on cleanup. Designed to be called after any structural
 * mutation (section/question delete, type change, option edit, reorder).
 */
export function applyVisibilityCleanup(
	getValues: (path: string) => unknown,
	setValue: (path: string, value: unknown, options?: { shouldDirty?: boolean }) => void
): void {
	const current = (getValues('surveyForm.surveyParts') as SurveyPart[] | undefined) ?? [];
	if (current.length === 0) return;

	const cleaned = cleanupVisibilityRules(current);
	const sameVw = (a: QuestionVisibility | undefined, b: QuestionVisibility | undefined): boolean => {
		if (a === b) return true;
		if (!a || !b) return false;
		return JSON.stringify(a) === JSON.stringify(b);
	};

	for (let i = 0; i < current.length; i++) {
		const curr = current[i];
		const next = cleaned[i];
		if (!curr || !next) continue;
		if (!sameVw(curr.visibleWhen, next.visibleWhen)) {
			setValue(`surveyForm.surveyParts.${i}.visibleWhen`, next.visibleWhen, { shouldDirty: true });
		}
		if (isSurveySection(curr) && isSurveySection(next)) {
			const cqs = curr.questions ?? [];
			const nqs = next.questions ?? [];
			for (let j = 0; j < cqs.length; j++) {
				if (!sameVw(cqs[j]?.visibleWhen, nqs[j]?.visibleWhen)) {
					setValue(`surveyForm.surveyParts.${i}.questions.${j}.visibleWhen`, nqs[j]?.visibleWhen, {
						shouldDirty: true,
					});
				}
			}
		}
	}
}
